/**
 * Feature flag utilities for controlled rollout of new API endpoints
 */

/**
 * Simple string hash function to generate consistent hash codes
 * Used for percentage-based feature flag rollout
 */
export function hashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i += 1) {
    const char = str.charCodeAt(i)
    // Use 31 as multiplier (prime number reduces collisions)
    // This is the same algorithm as Java's String.hashCode()
    // eslint-disable-next-line no-bitwise
    hash = (31 * hash + char) | 0 // | 0 converts to 32-bit integer to prevent collisions and poor distribution
  }
  return Math.abs(hash)
}

/**
 * Determines if a respondent should use a new endpoint based on a percentage rollout.
 * Uses hash-based deterministic assignment for consistent experience per respondent.
 *
 * @param rolloutEnvValue - The raw env variable value (e.g. '0', '50', '100')
 * @param respondentId - The unique identifier for the respondent
 * @returns true if respondent should use the new endpoint
 */
function shouldUseEndpoint(
  rolloutEnvValue: string | undefined,
  respondentId: string
): boolean {
  const rolloutPercentage = parseInt(rolloutEnvValue || '0', 10)

  if (rolloutPercentage < 0 || rolloutPercentage > 100) {
    return false
  }

  if (rolloutPercentage === 0) {
    return false
  }

  if (rolloutPercentage === 100) {
    return true
  }

  const hash = hashCode(respondentId)
  return hash % 100 < rolloutPercentage
}

/**
 * Determines if a respondent should use new API endpoints for categorical questions.
 * Controlled by REACT_APP_NEW_API_ROLLOUT_PERCENTAGE.
 *
 * @param respondentId - The unique identifier for the respondent
 * @returns true if respondent should use new endpoints, false for legacy endpoints
 */
export function shouldUseNewApiEndpoints(respondentId: string): boolean {
  return shouldUseEndpoint(
    process.env.REACT_APP_NEW_API_ROLLOUT_PERCENTAGE,
    respondentId
  )
}

/**
 * Determines if a respondent should use new response options API endpoints
 * (slider, free text, ranked, max diff, terms & conditions).
 * Controlled by REACT_APP_RESPONSE_OPTIONS_NEW_API_ROLLOUT.
 *
 * Separate from shouldUseNewApiEndpoints which controls categorical question routing.
 *
 * @param respondentId - The unique identifier for the respondent
 * @returns true if respondent should use new response options endpoints
 */
export function shouldUseResponseOptionsEndpoints(
  respondentId: string
): boolean {
  return shouldUseEndpoint(
    process.env.REACT_APP_RESPONSE_OPTIONS_NEW_API_ROLLOUT,
    respondentId
  )
}

/**
 * Determines if a respondent should use the new questionnaire API endpoint.
 * Controlled by REACT_APP_QUESTIONNAIRE_NEW_API_ROLLOUT.
 *
 * @param respondentId - The unique identifier for the respondent
 * @returns true if respondent should use new questionnaire endpoint
 */
export function shouldUseQuestionnaireEndpoint(respondentId: string): boolean {
  return shouldUseEndpoint(
    process.env.REACT_APP_QUESTIONNAIRE_NEW_API_ROLLOUT,
    respondentId
  )
}
